/**
 * Formulaire des informations d'une SKILLCARD (spec § 4) — partagé par la
 * création (`/tutor/cards/new`) et l'édition (détail d'une carte).
 *
 * En tête : « Générer la carte » — le tuteur écrit un simple texte (niveau,
 * méthode, notes…), l'IA remplit TOUS les champs (y compris XP max,
 * difficulté, importance, rareté), modifiables ensuite ; régénérable à volonté. Le
 * texte est conservé dans la carte (`source_text`, migration 0273).
 *
 * Les niveaux sont INDÉPENDANTS de la matière (spec § 14) : tous les niveaux
 * sont proposés, une carte peut servir de la 3e à la 2nde.
 */

import { useState, type FormEvent } from 'react';
import { Sparkles } from 'lucide-react';
import { Button, cn } from '@jeraisonne/ui';
import { SCHOOL_LEVELS, SUBJECTS } from '@jeraisonne/db';
import {
  CARD_COLOR_THEMES,
  CARD_COLOR_THEME_LABELS,
  CARD_DIFFICULTIES,
  CARD_DIFFICULTY_LABELS,
  CARD_IMPORTANCES,
  CARD_IMPORTANCE_LABELS,
  CARD_NIGHT_PALETTE,
  CARD_RARITIES,
  CARD_RARITY_LABELS,
  CARD_RARITY_STARS,
  cardPalette,
  CARD_TYPES,
  CARD_TYPE_LABELS,
  CARD_XP_DEFAULT,
  CARD_QUESTION_XP_DEFAULT,
  CARD_QUESTION_XP_MAX,
  CARD_QUESTION_XP_MIN,
  stripMath,
} from '@jeraisonne/db/cards';
import {
  useCardRelations,
  useCards,
  useGenerateCardInfo,
  useLessonOptions,
  type CardInput,
} from '@/lib/cards/api';
import {
  inheritedRarity,
  parentCandidates,
  parentIdsOf,
  rarityFromRelations,
} from '@/lib/cards/filiation';
import {
  applyGeneratedCardInfo,
  CARD_SOURCE_TEXT_MAX,
  CARD_SOURCE_TEXT_MIN,
} from '@/lib/cards/card-info';
import {
  SkillcardPickerField,
  toSkillcardPickerItem,
} from '@/features/skillcards/skillcard-picker';

export const EMPTY_CARD_INPUT: CardInput = {
  title: '',
  card_type: 'methode',
  subject: 'math',
  topic: '',
  domain_label: null,
  when_to_use: '',
  rule: '',
  method: '',
  example: '',
  pitfalls: '',
  source_text: '',
  xp_max: CARD_XP_DEFAULT,
  difficulty: 'intermediaire',
  importance: 'importante',
  rarity: 'normale',
  color_theme: 'level',
  parent_card_id: null,
  question_xp: null,
  levels: [],
  lesson_ids: [],
};

/** Problèmes bloquants du formulaire (le reste est validé par la base). */
export function cardInputIssues(input: CardInput): string[] {
  const issues: string[] = [];
  if (!input.title.trim()) issues.push('Le titre est obligatoire.');
  if (input.levels.length === 0) issues.push('Choisis au moins un niveau.');
  if (
    input.question_xp !== null &&
    (!Number.isInteger(input.question_xp) ||
      input.question_xp < CARD_QUESTION_XP_MIN ||
      input.question_xp > CARD_QUESTION_XP_MAX)
  ) {
    issues.push(
      `L’XP par bonne réponse doit être un entier entre ${CARD_QUESTION_XP_MIN} et ${CARD_QUESTION_XP_MAX} (ou vide).`,
    );
  }
  return issues;
}

const inputClass =
  'border-ink-200 text-ink-900 focus:border-primary-400 min-h-[2.75rem] w-full rounded-lg border bg-white px-3 py-2 text-sm outline-none';

function Label({ children, hint }: { children: string; hint?: string }) {
  return (
    <span className="block">
      <span className="text-ink-700 text-sm font-semibold">{children}</span>
      {hint && <span className="text-ink-400 block text-xs">{hint}</span>}
    </span>
  );
}

export interface CardInfoFormProps {
  initial: CardInput;
  /** Carte éditée (exclue, avec ses descendantes, des parentes possibles). */
  cardId?: string;
  submitLabel: string;
  submitting?: boolean;
  error?: string | null;
  onSubmit: (input: CardInput) => void;
  onCancel?: () => void;
}

export function CardInfoForm({
  initial,
  cardId,
  submitLabel,
  submitting,
  error,
  onSubmit,
  onCancel,
}: CardInfoFormProps) {
  const [input, setInput] = useState<CardInput>(initial);
  const [showIssues, setShowIssues] = useState(false);
  const lessons = useLessonOptions(input.subject, input.levels);
  const issues = cardInputIssues(input);
  const generateInfo = useGenerateCardInfo();
  const [generatedCount, setGeneratedCount] = useState(0);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const sourceReady = input.source_text.trim().length >= CARD_SOURCE_TEXT_MIN;
  const cards = useCards();
  const relations = useCardRelations();
  const edges = relations.data ?? [];
  const candidates = parentCandidates(cards.data ?? [], edges, cardId ?? null, input.subject);
  const parent = (cards.data ?? []).find((c) => c.id === input.parent_card_id) ?? null;
  // Édition : mères gérées dans le bloc Filiation (N-N) → rareté de TOUTES les mères.
  // Création : une seule mère possible ici (la 1re), les suivantes après coup.
  const editParentIds = cardId ? parentIdsOf(edges, cardId) : [];
  const lockedRarity = cardId
    ? rarityFromRelations(cards.data ?? [], edges, cardId)
    : input.parent_card_id
      ? inheritedRarity(parent)
      : null;
  const editParentTitles = (cards.data ?? [])
    .filter((c) => editParentIds.includes(c.id))
    .map((c) => stripMath(c.title));

  const setParent = (id: string) => {
    const next = (cards.data ?? []).find((c) => c.id === id) ?? null;
    setInput((prev) => ({
      ...prev,
      parent_card_id: next ? next.id : null,
      rarity: inheritedRarity(next) ?? prev.rarity,
    }));
  };

  const generate = async () => {
    setGenerateError(null);
    try {
      const info = await generateInfo.mutateAsync({ source_text: input.source_text.trim() });
      setInput((prev) => applyGeneratedCardInfo(prev, info));
      setGeneratedCount((n) => n + 1);
      setShowIssues(false);
    } catch (e) {
      setGenerateError(e instanceof Error ? e.message : 'Génération impossible.');
    }
  };

  const set = <K extends keyof CardInput>(key: K, value: CardInput[K]) =>
    setInput((prev) => ({ ...prev, [key]: value }));

  const toggle = (key: 'levels' | 'lesson_ids', value: string) =>
    setInput((prev) => ({
      ...prev,
      [key]: prev[key].includes(value)
        ? prev[key].filter((v) => v !== value)
        : [...prev[key], value],
    }));

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (issues.length > 0) {
      setShowIssues(true);
      return;
    }
    onSubmit({
      ...input,
      title: input.title.trim(),
      domain_label: input.domain_label?.trim() || null,
    });
  };

  return (
    <form onSubmit={submit} className="space-y-5">
      <section
        aria-labelledby="card-source-title"
        className="border-primary-200 space-y-3 rounded-2xl border bg-white p-4"
      >
        <div className="flex items-start gap-2">
          <Sparkles className="text-primary-500 mt-0.5 h-5 w-5 shrink-0" aria-hidden />
          <div>
            <h2 id="card-source-title" className="text-ink-900 text-sm font-bold">
              Générer la carte à partir d’un texte
            </h2>
            <p className="text-ink-500 text-xs">
              Écris le niveau, la règle ou la méthode, en vrac. L’IA remplit toutes les informations
              (niveaux, méthode, HORA, difficulté, importance…). Tu peux tout modifier et régénérer
              autant de fois que tu veux.
            </p>
          </div>
        </div>
        <label className="block">
          <span className="sr-only">Texte de la carte</span>
          <textarea
            className={inputClass}
            rows={5}
            maxLength={CARD_SOURCE_TEXT_MAX}
            placeholder="3e — équation produit nul : un produit de facteurs est nul si et seulement si l’un des facteurs est nul. Piège : développer au lieu de factoriser."
            value={input.source_text}
            onChange={(e) => set('source_text', e.target.value)}
          />
        </label>
        <Button
          type="button"
          variant="accent"
          fullWidth
          className="sm:w-auto"
          loading={generateInfo.isPending}
          disabled={!sourceReady || generateInfo.isPending}
          onClick={generate}
        >
          <Sparkles className="h-4 w-4" aria-hidden />
          {generatedCount > 0 || initial.source_text.trim()
            ? 'Régénérer la carte'
            : 'Générer la carte'}
        </Button>
        {generatedCount > 0 && !generateError && (
          <p role="status" className="text-ink-500 text-xs">
            Informations proposées par l’IA : relis-les et modifie ce que tu veux ci-dessous.
          </p>
        )}
        {generateError && (
          <p role="alert" className="text-accent-600 text-sm">
            {generateError}
          </p>
        )}
      </section>

      <label className="block space-y-1">
        <Label>Titre</Label>
        <input
          className={inputClass}
          value={input.title}
          maxLength={120}
          placeholder="Résoudre une équation produit nul"
          onChange={(e) => set('title', e.target.value)}
        />
      </label>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label className="block space-y-1">
          <Label>Type</Label>
          <select
            className={inputClass}
            value={input.card_type}
            onChange={(e) => set('card_type', e.target.value as CardInput['card_type'])}
          >
            {CARD_TYPES.map((t) => (
              <option key={t} value={t}>
                {CARD_TYPE_LABELS[t]}
              </option>
            ))}
          </select>
        </label>
        <label className="block space-y-1">
          <Label>Matière</Label>
          <select
            className={inputClass}
            value={input.subject}
            onChange={(e) => setInput((p) => ({ ...p, subject: e.target.value, lesson_ids: [] }))}
          >
            {SUBJECTS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <fieldset className="space-y-2">
        <legend>
          <Label hint="Plusieurs niveaux possibles : la carte est indépendante du programme.">
            Niveau(x)
          </Label>
        </legend>
        <div className="flex flex-wrap gap-2">
          {SCHOOL_LEVELS.map((l) => {
            const on = input.levels.includes(l.value);
            return (
              <button
                key={l.value}
                type="button"
                aria-pressed={on}
                onClick={() => toggle('levels', l.value)}
                className={cn(
                  'min-h-[2.75rem] min-w-[2.75rem] rounded-full border px-3 text-sm font-semibold transition-colors active:scale-[0.98]',
                  on
                    ? 'border-primary-500 bg-primary-500 text-white'
                    : 'border-ink-200 text-ink-700 bg-white',
                )}
              >
                {l.label}
              </button>
            );
          })}
        </div>
      </fieldset>

      <label className="block space-y-1">
        <Label hint="Ce que l’outil permet de comprendre ou de résoudre.">Sujet / compétence</Label>
        <input
          className={inputClass}
          value={input.topic}
          maxLength={200}
          placeholder="Équations"
          onChange={(e) => set('topic', e.target.value)}
        />
      </label>

      <label className="block space-y-1">
        <Label hint="Affiché au verso sous les niveaux (optionnel).">Chapitre / domaine</Label>
        <input
          className={inputClass}
          value={input.domain_label ?? ''}
          maxLength={80}
          placeholder="Calcul littéral"
          onChange={(e) => set('domain_label', e.target.value)}
        />
      </label>

      {(
        [
          ['method', 'Méthode / règle', 'Le savoir à transformer en outil.', 5, 4000],
          [
            'when_to_use',
            'Quand l’utiliser ?',
            'Indices qui font reconnaître la situation.',
            3,
            2000,
          ],
          [
            'rule',
            'Règle détaillée',
            'Affichée en entier sur la page en ligne (optionnel).',
            4,
            4000,
          ],
          ['example', 'Exemple(s)', 'Un ou deux exemples courts.', 3, 2000],
          ['pitfalls', 'Pièges / erreurs', 'Erreurs fréquentes des élèves.', 3, 2000],
        ] as const
      ).map(([key, label, hint, rows, max]) => (
        <label key={key} className="block space-y-1">
          <Label hint={hint}>{label}</Label>
          <textarea
            className={inputClass}
            rows={rows}
            maxLength={max}
            value={input[key]}
            onChange={(e) => set(key, e.target.value)}
          />
        </label>
      ))}

      <label className="block space-y-1">
        <Label
          hint={`Gagnée par la carte à chaque bonne réponse à une question reliée (×1,5 si la question est moyenne, ×2 si elle est difficile). Vide = ${CARD_QUESTION_XP_DEFAULT} HORA.`}
        >
          HORA par bonne réponse
        </Label>
        <input
          type="number"
          inputMode="numeric"
          className={inputClass}
          min={CARD_QUESTION_XP_MIN}
          max={CARD_QUESTION_XP_MAX}
          step={1}
          placeholder={String(CARD_QUESTION_XP_DEFAULT)}
          value={input.question_xp ?? ''}
          onChange={(e) =>
            set('question_xp', e.target.value === '' ? null : Number(e.target.value))
          }
        />
      </label>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {/* HORA max CALCULÉ (migration 0508) : somme des HORA des exercices reliés. */}
        <div className="block space-y-1" data-testid="card-hora-max">
          <Label hint="Somme des HORA de tous les exercices reliés, recalculée automatiquement.">
            HORA maximum
          </Label>
          <p className="border-ink-100 bg-ink-50 text-ink-700 flex min-h-[2.75rem] items-center rounded-lg border px-3 text-sm">
            {cardId ? `${input.xp_max} HORA` : 'Calculé dès que des exercices sont reliés'}
          </p>
        </div>
        <label className="block space-y-1">
          <Label>Difficulté</Label>
          <select
            className={inputClass}
            value={input.difficulty}
            onChange={(e) => set('difficulty', e.target.value as CardInput['difficulty'])}
          >
            {CARD_DIFFICULTIES.map((d) => (
              <option key={d} value={d}>
                {CARD_DIFFICULTY_LABELS[d]}
              </option>
            ))}
          </select>
        </label>
        <label className="block space-y-1">
          <Label>Importance</Label>
          <select
            className={inputClass}
            value={input.importance}
            onChange={(e) => set('importance', e.target.value as CardInput['importance'])}
          >
            {CARD_IMPORTANCES.map((i) => (
              <option key={i} value={i}>
                {CARD_IMPORTANCE_LABELS[i]}
              </option>
            ))}
          </select>
        </label>
      </div>

      {cardId ? (
        <p className="bg-ink-50 text-ink-600 rounded-lg px-3 py-2 text-sm">
          {editParentIds.length > 0
            ? `Cartes mères : ${editParentTitles.join(', ')}. `
            : 'Aucune carte mère. '}
          Ajoute ou retire des cartes mères dans le bloc « Filiation » de la carte.
        </p>
      ) : (
        <div className="block space-y-1">
          <Label hint="Si cette carte est la suite plus avancée d’une autre (distributivité → double distributivité). Elle gagne alors une étoile de rareté de plus que sa mère. D’autres mères pourront être ajoutées ensuite.">
            Carte mère (filiation)
          </Label>
          <SkillcardPickerField
            items={candidates.map(toSkillcardPickerItem)}
            value={input.parent_card_id ?? null}
            onChange={(id) => setParent(id ?? '')}
            noneLabel="Aucune — carte de départ"
            fallbackLabel={parent?.title ?? 'Carte parente'}
            title="Choisir la carte mère"
          />
        </div>
      )}

      <fieldset className="space-y-2">
        <legend>
          <Label
            hint={
              lockedRarity
                ? cardId
                  ? 'Héritée : la plus rare des cartes mères + 1 étoile.'
                  : `Héritée : « ${parent?.title ?? 'carte mère'} » + 1 étoile.`
                : 'Les étoiles du verso : de ★ (simple) à ★★★★★ (légendaire).'
            }
          >
            Rareté
          </Label>
        </legend>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-5">
          {CARD_RARITIES.map((r) => {
            const on = (lockedRarity ?? input.rarity) === r;
            const stars = CARD_RARITY_STARS[r];
            return (
              <button
                key={r}
                type="button"
                aria-pressed={on}
                aria-label={`${CARD_RARITY_LABELS[r]} (${stars} étoile${stars > 1 ? 's' : ''})`}
                disabled={!!lockedRarity && !on}
                onClick={() => set('rarity', r)}
                className={cn(
                  'disabled:opacity-40',
                  'flex min-h-[2.75rem] items-center justify-between gap-2 rounded-lg border px-3 py-2 text-left text-sm font-semibold transition-colors active:scale-[0.98] sm:flex-col sm:justify-center sm:text-center',
                  on
                    ? 'border-primary-500 bg-primary-50 text-ink-900'
                    : 'border-ink-200 text-ink-700 bg-white',
                )}
              >
                <span>{CARD_RARITY_LABELS[r]}</span>
                <span aria-hidden className="text-xs tracking-wider text-amber-500">
                  {'★'.repeat(stars)}
                  <span className="text-ink-200">{'★'.repeat(5 - stars)}</span>
                </span>
              </button>
            );
          })}
        </div>
      </fieldset>

      <fieldset className="space-y-2">
        <legend>
          <Label hint="Par défaut la teinte du niveau le plus bas (6ème violet … Terminale rouge).">
            Couleur de la carte
          </Label>
        </legend>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {CARD_COLOR_THEMES.map((t) => {
            const on = input.color_theme === t;
            const swatch = t === 'night' ? CARD_NIGHT_PALETTE : cardPalette(input.levels, 'level');
            return (
              <button
                key={t}
                type="button"
                aria-pressed={on}
                onClick={() => set('color_theme', t)}
                className={cn(
                  'flex min-h-[2.75rem] items-center gap-3 rounded-lg border px-3 py-2 text-left text-sm font-semibold transition-colors active:scale-[0.98]',
                  on
                    ? 'border-primary-500 bg-primary-50 text-ink-900'
                    : 'border-ink-200 text-ink-700 bg-white',
                )}
              >
                <span
                  aria-hidden
                  className="h-6 w-6 shrink-0 rounded-full border-2"
                  style={{ background: swatch.bg, borderColor: swatch.accent }}
                />
                <span>
                  {CARD_COLOR_THEME_LABELS[t]}
                  <span className="text-ink-400 block text-xs font-normal">
                    {t === 'level' && swatch.key === 'night' ? 'Choisis un niveau' : swatch.label}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </fieldset>

      <fieldset className="space-y-2">
        <legend>
          <Label hint="Leçons publiées de la matière et des niveaux choisis.">Cours associés</Label>
        </legend>
        {input.levels.length === 0 ? (
          <p className="text-ink-400 text-sm">Choisis d’abord un niveau.</p>
        ) : lessons.isLoading ? (
          <p className="text-ink-400 text-sm">Chargement des cours…</p>
        ) : (lessons.data ?? []).length === 0 ? (
          <p className="text-ink-400 text-sm">Aucun cours publié pour ces niveaux.</p>
        ) : (
          <div className="border-ink-200 max-h-56 space-y-1 overflow-y-auto rounded-lg border bg-white p-2">
            {(lessons.data ?? []).map((l) => (
              <label
                key={l.id}
                className="hover:bg-ink-50 flex min-h-[2.75rem] cursor-pointer items-center gap-3 rounded-md px-2"
              >
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={input.lesson_ids.includes(l.id)}
                  onChange={() => toggle('lesson_ids', l.id)}
                />
                <span className="text-ink-800 text-sm">
                  {l.title}
                  <span className="text-ink-400 block text-xs">
                    {l.chapterTitle} · {l.level}
                  </span>
                </span>
              </label>
            ))}
          </div>
        )}
      </fieldset>

      {showIssues && issues.length > 0 && (
        <ul role="alert" className="text-accent-600 list-disc space-y-1 pl-5 text-sm">
          {issues.map((i) => (
            <li key={i}>{i}</li>
          ))}
        </ul>
      )}
      {error && (
        <p role="alert" className="text-accent-600 text-sm">
          {error}
        </p>
      )}

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        {onCancel && (
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            fullWidth
            className="sm:w-auto"
          >
            Annuler
          </Button>
        )}
        <Button type="submit" loading={submitting} fullWidth className="sm:w-auto">
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
